(function () {
  'use strict';

  // A standalone file rather than an extension of quote-app.js (2026-07-21) — keeps this
  // feature isolated from the already-large, manually-synced single-quote portal file (see
  // CLAUDE.md's incident notes on quote-app.js drift). Same manual-theme-upload caveat
  // applies here too: this file must be re-uploaded to the Shopify theme after any change,
  // it does not auto-deploy from Railway/git push.

  var API_URL = (window.QUOTE_API_URL || '').replace(/\/$/, '');

  var container = document.getElementById('custom-orders-list-app');
  if (!container) return;

  // Sourced server-side from the customer's own logged-in Shopify session via Liquid
  // (`{{ customer.email | json }}` on the /pages/my-orders theme page) — empty when logged
  // out. No separate portal login/magic-link system; Shopify's own account login is the
  // entire auth mechanism here (confirmed 2026-07-21 — every customer who'd want to reorder
  // already has a Shopify account created automatically at submission time).
  var customerEmail = window.CUSTOMER_EMAIL || '';

  injectStyles();

  if (!customerEmail) {
    renderLoggedOut();
  } else {
    loadOrders(customerEmail);
  }

  function renderLoggedOut() {
    container.innerHTML =
      '<div class="co-wrap"><div class="co-alert">Please <a href="/account/login">log in</a> to view your orders.</div></div>';
  }

  function loadOrders(email) {
    container.innerHTML = '<div class="co-wrap"><p class="co-sub" style="text-align:center;padding:32px 0">Loading your orders…</p></div>';
    fetch(API_URL + '/api/account/orders?email=' + encodeURIComponent(email))
      .then(function (r) {
        if (!r.ok) throw new Error('error');
        return r.json();
      })
      .then(function (orders) { renderOrders(orders); })
      .catch(function () {
        container.innerHTML =
          '<div class="co-wrap"><div class="co-alert co-alert-error">Could not load your orders. Please refresh the page or try again later.</div></div>';
      });
  }

  // [css class, label] — same status vocabulary as quote-app.js's status badges (In
  // Progress / Mockups Ready / Order Placed / Shipped), independently maintained since the
  // two files are independently deployed.
  var STATUS_LABELS = {
    submitted: ['co-status-pending', 'In Progress'],
    in_review: ['co-status-pending', 'In Progress'],
    mockup_ready: ['co-status-ready', 'Mockups Ready'],
    approved: ['co-status-approved', 'Order Placed'],
    invoiced: ['co-status-approved', 'Order Placed'],
    shipped: ['co-status-shipped', 'Shipped'],
  };

  function renderOrders(orders) {
    if (!orders.length) {
      container.innerHTML =
        '<div class="co-wrap"><h1 class="co-heading">Your Orders</h1>' +
        '<p class="co-sub">You don\'t have any orders yet. <a href="/pages/custom-quote">Start a custom order →</a></p></div>';
      return;
    }
    var html = '<div class="co-wrap"><h1 class="co-heading">Your Orders</h1>';
    orders.forEach(function (order) { html += renderOrderCard(order); });
    html += '</div>';
    container.innerHTML = html;
    setupListeners();
  }

  function fmtMoney(amount) {
    return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Per-unit price for a reorder line — same convention quote-app.js's priceCellHtml()
  // already uses: retail struck through next to the real (wholesale/volume-discounted)
  // price whenever the two differ, otherwise just the plain price. Missing price data
  // (unit_price undefined — a SKU whose price couldn't be resolved) renders nothing rather
  // than a misleading "$0.00".
  function priceCellHtml(unitPrice, discountedUnit) {
    if (unitPrice == null || discountedUnit == null) return '';
    if (Math.round(unitPrice * 100) !== Math.round(discountedUnit * 100)) {
      return '<span class="co-price-strike">' + fmtMoney(unitPrice) + '</span> ' + fmtMoney(discountedUnit);
    }
    return fmtMoney(discountedUnit);
  }

  function renderOrderCard(order) {
    var statusInfo = STATUS_LABELS[order.status] || ['co-status-pending', order.status];
    var thumb = order.mockup_urls && order.mockup_urls[0];
    var skuLines = (order.resolved_skus || []).map(function (s) {
      return escHtml((s.color || s.title || s.sku) + ' × ' + (s.quantity || '?'));
    }).join(', ');

    var html = '<div class="co-card">';
    html += '<div class="co-card-main">';
    if (thumb) html += '<img class="co-thumb" src="' + escHtml(thumb) + '">';
    else html += '<div class="co-thumb-noimg"></div>';
    html += '<div class="co-card-body">';
    html += '<div class="co-status-badge ' + statusInfo[0] + '">' + escHtml(statusInfo[1]) + '</div>';
    html += '<div class="co-product-name">' + escHtml(order.product_name || 'Custom Order') + '</div>';
    if (skuLines) html += '<div class="co-sku-list">' + skuLines + '</div>';
    html += '<div class="co-date">Placed ' + escHtml((order.created_at || '').slice(0, 10)) + '</div>';
    html += '</div>';
    if (order.total != null) html += '<div class="co-total">' + fmtMoney(order.total) + '</div>';
    html += '</div>';
    if (order.reorder_eligible) {
      html += (
        '<button type="button" class="co-reorder-btn" data-order-id="' + escHtml(order.id) + '">' +
          '<span>Reorder</span><span class="co-reorder-chevron">▾</span>' +
        '</button>'
      );
      html += renderReorderPicker(order);
    }
    html += '</div>';
    return html;
  }

  // A short checklist of just THIS order's own SKUs, pre-checked/pre-filled with the
  // original quantities — not the full catalog grid (no search needed, only a handful of
  // rows) since a reorder can only ever reuse SKUs that were actually part of the order.
  // Row layout mirrors quote-app.js's own Products & Pricing quantity rows (.cq-qty-row —
  // thumbnail, label/sub, quantity input) so this reads as the same product the customer
  // already knows from checkout, just with a checkbox added to pick a subset.
  function renderReorderPicker(order) {
    var rows = (order.resolved_skus || []).map(function (s) {
      var label = s.color || s.title || s.sku;
      var sub = s.color && s.title ? s.title : s.sku;
      var img = s.image_url
        ? '<img src="' + escHtml(s.image_url) + '" alt="">'
        : '<div class="co-reorder-noimg"></div>';
      var customLine = priceCellHtml(s.customization_unit_price, s.customization_discounted_unit_price);
      return (
        '<div class="co-reorder-row">' +
          '<input type="checkbox" class="co-reorder-check" data-sku="' + escHtml(s.sku) + '" checked>' +
          img +
          '<div class="co-reorder-info">' +
            '<div class="co-reorder-label">' + escHtml(label) + '</div>' +
            '<div class="co-reorder-sub">' + escHtml(sub) + '</div>' +
          '</div>' +
          '<div class="co-reorder-price">' +
            '<div class="co-reorder-price-line">' + priceCellHtml(s.unit_price, s.discounted_unit_price) + '</div>' +
            (customLine ? '<div class="co-reorder-price-line co-reorder-price-sub">+ ' + customLine + ' customization</div>' : '') +
          '</div>' +
          '<input type="number" min="1" class="co-reorder-qty" data-sku="' + escHtml(s.sku) + '" value="' + (s.quantity || 1) + '">' +
        '</div>'
      );
    }).join('');
    return (
      '<div class="co-reorder-picker" id="co-reorder-picker-' + escHtml(order.id) + '" style="display:none">' +
        '<div class="co-reorder-rows">' + rows + '</div>' +
        '<div class="co-alert co-alert-error" id="co-reorder-error-' + escHtml(order.id) + '" style="display:none"></div>' +
        '<button type="button" class="co-reorder-submit" data-order-id="' + escHtml(order.id) + '">Confirm Reorder</button>' +
      '</div>'
    );
  }

  function setupListeners() {
    container.querySelectorAll('.co-reorder-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var picker = document.getElementById('co-reorder-picker-' + btn.dataset.orderId);
        if (!picker) return;
        var opening = picker.style.display === 'none';
        picker.style.display = opening ? '' : 'none';
        btn.classList.toggle('co-reorder-btn-open', opening);
      });
    });
    container.querySelectorAll('.co-reorder-submit').forEach(function (btn) {
      btn.addEventListener('click', function () { submitReorder(btn.dataset.orderId, btn); });
    });
  }

  function submitReorder(orderId, btn) {
    var errorEl = document.getElementById('co-reorder-error-' + orderId);
    var picker = document.getElementById('co-reorder-picker-' + orderId);
    errorEl.style.display = 'none';

    var skus = [];
    picker.querySelectorAll('.co-reorder-check').forEach(function (chk) {
      if (!chk.checked) return;
      var qtyInput = picker.querySelector('.co-reorder-qty[data-sku="' + chk.dataset.sku + '"]');
      var qty = parseInt(qtyInput.value, 10);
      if (qty >= 1) skus.push({ sku: chk.dataset.sku, quantity: qty });
    });
    if (!skus.length) {
      errorEl.textContent = 'Select at least one item to reorder.';
      errorEl.style.display = 'block';
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Placing Reorder…';

    fetch(API_URL + '/api/quotes/' + encodeURIComponent(orderId) + '/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: customerEmail, skus: skus }),
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.error || 'Failed to reorder'); });
        return r.json();
      })
      .then(function (data) {
        // Lands on the normal single-quote portal page, already at mockup_ready — the
        // existing quote-app.js takes over from here completely unmodified.
        window.location.href = data.quote_url;
      })
      .catch(function (err) {
        errorEl.textContent = err.message;
        errorEl.style.display = 'block';
        btn.disabled = false;
        btn.textContent = 'Confirm Reorder';
      });
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function injectStyles() {
    if (document.getElementById('co-styles')) return;
    var style = document.createElement('style');
    style.id = 'co-styles';
    style.textContent = [
      '.co-wrap { max-width: 720px; margin: 0 auto; padding: 24px 16px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }',
      '.co-heading { font-size: 26px; margin-bottom: 20px; color: #182e49; }',

      // Order card — same frost/navy language as quote-app.js's .cq-card.
      '.co-card { background: #edf0f3; border: 1px solid #d0d9e3; border-radius: 8px; padding: 18px 22px; margin-bottom: 14px; }',
      '.co-card-main { display: flex; align-items: flex-start; gap: 14px; }',
      '.co-thumb, .co-thumb-noimg { width: 64px; height: 64px; object-fit: cover; border-radius: 6px; background: #fff; flex-shrink: 0; }',
      '.co-card-body { flex: 1; min-width: 0; }',
      '.co-status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 8px; }',
      '.co-status-pending { background: #fef3c7; color: #92400e; }',
      '.co-status-ready { background: #182e49; color: white; }',
      '.co-status-approved { background: #f0a955; color: white; }',
      '.co-status-shipped { background: #edf0f3; color: #182e49; border: 1px solid #d0d9e3; }',
      '.co-product-name { font-weight: 700; font-size: 15px; color: #182e49; margin-bottom: 4px; }',
      '.co-sku-list { font-size: 13px; color: #555; margin-bottom: 4px; }',
      '.co-date { font-size: 12px; color: #6b7280; }',
      '.co-total { font-size: 16px; font-weight: 700; color: #182e49; white-space: nowrap; }',

      // Reorder — an accordion header (chevron rotates, bottom corners flatten when open so
      // it visually connects to the picker panel dropping down beneath it) rather than a
      // plain button, so it reads as "this expands" at a glance.
      '.co-reorder-btn { margin-top: 14px; width: 100%; display: flex; align-items: center; justify-content: space-between; background: #182e49; color: white; border: none; border-radius: 6px; padding: 10px 16px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; }',
      '.co-reorder-btn-open { border-bottom-left-radius: 0; border-bottom-right-radius: 0; }',
      '.co-reorder-chevron { transition: transform .15s; }',
      '.co-reorder-btn-open .co-reorder-chevron { transform: rotate(180deg); }',
      '.co-reorder-picker { background: #fff; border: 1px solid #d0d9e3; border-top: none; border-radius: 0 0 6px 6px; padding: 6px 16px 16px; }',

      // Reorder rows — same shape as quote-app.js's .cq-qty-row (thumbnail, label/sub,
      // quantity input), with a checkbox prepended so a subset of the order can be picked.
      '.co-reorder-rows { }',
      '.co-reorder-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid #edf0f3; }',
      '.co-reorder-rows .co-reorder-row:last-child { border-bottom: none; }',
      '.co-reorder-check { width: 18px; height: 18px; flex-shrink: 0; }',
      '.co-reorder-row img, .co-reorder-noimg { width: 48px; height: 48px; object-fit: cover; border-radius: 6px; background: #edf0f3; flex-shrink: 0; }',
      '.co-reorder-info { flex: 1; min-width: 0; }',
      '.co-reorder-label { font-size: 14px; font-weight: 600; color: #182e49; }',
      '.co-reorder-sub { font-size: 12px; color: #6b7280; }',
      '.co-reorder-price { font-size: 13px; color: #182e49; white-space: nowrap; text-align: right; flex-shrink: 0; }',
      '.co-reorder-price-line { line-height: 1.4; }',
      '.co-reorder-price-sub { font-size: 11px; color: #6b7280; }',
      '.co-price-strike { color: #9ca3af; text-decoration: line-through; margin-right: 4px; }',
      '.co-reorder-qty { width: 60px; padding: 8px 10px; border: 1.5px solid #d0d9e3; border-radius: 6px; font-size: 14px; font-family: inherit; text-align: center; }',
      '.co-reorder-qty:focus { outline: none; border-color: #182e49; box-shadow: 0 0 0 3px rgba(24,46,73,.1); }',
      '.co-reorder-submit { margin-top: 12px; background: #f0a955; color: white; border: none; border-radius: 6px; padding: 10px 22px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; }',

      '.co-alert { padding: 12px 16px; border-radius: 6px; font-size: 14px; }',
      '.co-alert-error { background: #fee2e2; color: #991b1b; margin-top: 10px; }',
      '.co-sub { font-size: 14px; color: #555; }',
    ].join('\n');
    document.head.appendChild(style);
  }
})();
